import type { PlayerSnapshot } from '@wyn/contracts';
import { UI } from '../icons/ui-icons.ts';
import { Icon } from './Icon.tsx';
import { formatDuration } from '../lib/time.ts';

type BarName = keyof PlayerSnapshot['bars'];

export const BAR_META: Record<BarName, { label: string; icon: string; color: string }> = {
  life: { label: 'Life', icon: UI.life, color: 'bg-blood' },
  vigour: { label: 'Vigour', icon: UI.vigour, color: 'bg-verdigris' },
  nerve: { label: 'Nerve', icon: UI.nerve, color: 'bg-heather' },
  focus: { label: 'Focus', icon: UI.focus, color: 'bg-woad' },
  mood: { label: 'Mood', icon: UI.mood, color: 'bg-ochre' },
};

/** `skew` = serverTime − clientTime at fetch, so countdowns line up with the server's clock. */
export function BarMeter({ name, bar, now, skew }: { name: BarName; bar: PlayerSnapshot['bars'][BarName]; now: number; skew: number }) {
  const meta = BAR_META[name];
  const pct = Math.min(100, (bar.value / Math.max(1, bar.max)) * 100);
  const serverNow = now + skew;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="smallcaps text-ink-dim">
          <Icon name={meta.icon} className="mr-1" />
          {meta.label}
        </span>
        <span className="font-mono tabular-nums">
          {Math.floor(bar.value)}
          <span className="text-ink-faint">/{bar.max}</span>
        </span>
      </div>
      <div className="h-1.5 w-full bg-rule" role="meter" aria-label={meta.label} aria-valuenow={bar.value} aria-valuemin={0} aria-valuemax={bar.max}>
        <div className={`h-full ${meta.color} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <div className="h-4 text-right font-mono text-xs text-ink-faint">
        {bar.nextAt !== null && bar.fullAt !== null && (
          <>next {formatDuration(bar.nextAt - serverNow)} · full {formatDuration(bar.fullAt - serverNow)}</>
        )}
      </div>
    </div>
  );
}
